import SensitiveConsents, { SensitiveField, hasSensitiveConsent } from "@/components/SensitiveConsents";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, SkipForward } from "lucide-react";
import { useI18n } from "@/contexts/LanguageContext";
import { useProfile } from "@/contexts/ProfileContext";
import { api, extractError } from "@/lib/api";
import { LABELS, optionLabel, normalizeOrientation } from "@/lib/profileLabels";
import { Chip, ChipGroup, FieldLabel } from "@/components/Chip";
import PhotoManager from "@/components/PhotoManager";
import { ordinaryContent } from "@/components/ContentConsents";

const STEPS = ["basic", "looking_for", "about_me", "photos", "interests", "values", "lifestyle", "community"];
const TOTAL = STEPS.length;

const MISSING_LABELS = {
  it: {
    display_name: "Nome visualizzato",
    birthdate: "Data di nascita",
    relationship_goals: "Cosa cerchi",
    interested_in_genders: "Chi ti interessa",
  },
  en: {
    display_name: "Display name",
    birthdate: "Birthdate",
    relationship_goals: "What you're looking for",
    interested_in_genders: "Who you're interested in",
  },
};

const ERROR_CODES = {
  it: {
    invalid_age_range: "Il limite massimo di età deve essere maggiore o uguale al limite minimo.",
    unsupported_file_type: "Formato immagine non supportato. Usa JPG, PNG o WebP.",
    file_too_large: "Il file è troppo grande (max 10 MB).",
    invalid_image: "L'immagine non è valida.",
    max_photos_reached: "Hai raggiunto il numero massimo di foto (8).",
    storage_error: "Non è stato possibile caricare la foto. Riprova.",
    photo_not_found: "Foto non trovata.",
  },
  en: {
    invalid_age_range: "The maximum age must be greater than or equal to the minimum age.",
    unsupported_file_type: "Unsupported image format. Use JPG, PNG or WebP.",
    file_too_large: "File too large (max 10 MB).",
    invalid_image: "The image is not valid.",
    max_photos_reached: "You have reached the maximum number of photos (8).",
    storage_error: "We couldn't upload the photo. Please try again.",
    photo_not_found: "Photo not found.",
  },
};

function friendlyError(lang, err) {
  if (typeof err !== "string") return err;
  if (err.startsWith("missing_required:")) {
    const fields = err.slice("missing_required:".length).split(",").filter(Boolean);
    const labels = fields.map((f) => MISSING_LABELS[lang][f] || f);
    const prefix = lang === "it" ? "Compila i campi obbligatori: " : "Please fill the required fields: ";
    return prefix + labels.join(", ");
  }
  if (ERROR_CODES[lang][err]) return ERROR_CODES[lang][err];
  return err;
}

function StepShell({ step, total, title, subtitle, tag, children }) {
  return (
    <div>
      <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
        {tag} · {step} / {total}
      </p>
      <h2 className="mt-3 font-display text-3xl md:text-4xl leading-tight">{title}</h2>
      {subtitle ? (
        <p className="mt-3 text-sm md:text-base" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          {subtitle}
        </p>
      ) : null}
      <div className="mt-8">{children}</div>
    </div>
  );
}

export default function Onboarding() {
  const { lang, t } = useI18n();
  const { refresh } = useProfile();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [profile, setProfile] = useState(null);
  const [options, setOptions] = useState({});
  const [photos, setPhotos] = useState([]);

  // Section state
  const [basic, setBasic] = useState({ display_name: "", first_name: "", birthdate: "", gender: null, sexual_orientation: null, city: "", country: "", languages: [] });
  const [lookingFor, setLookingFor] = useState({ relationship_goals: [], interested_in_genders: [], age_min: 25, age_max: 55, distance_km: 100 });
  const [bio, setBio] = useState("");
  const [bioContentContext, setBioContentContext] = useState(ordinaryContent());
  const [interests, setInterests] = useState([]);
  const [values, setValues] = useState({ values: [], top_values: [] });
  const [lifestyle, setLifestyle] = useState({ smoking: null, alcohol: null, pets: null, lifestyle_type: null, diet: null });
  const [family, setFamily] = useState({ relationship_status: null, children: null, wants_children: null, open_to_children: null });
  const [education, setEducation] = useState({ education: null, profession: "" });
  const [religion, setReligion] = useState({ religion: null, religion_importance: null });
  const [community, setCommunity] = useState({ covid_perspective: null, covid_motivations: [], covid_public: false });
  const [comm, setComm] = useState({ communication_style: [] });

  // Load existing profile + options on mount
  useEffect(() => {
    (async () => {
      try {
        const [pRes, oRes] = await Promise.all([
          api.get("/profile/me"),
          api.get("/profile/options"),
        ]);
        setOptions(oRes.data || {});
        const p = pRes.data?.profile || {};
        setProfile(p);
        setBasic({
          display_name: p.display_name || "",
          first_name: p.first_name || "",
          birthdate: p.birthdate || "",
          gender: p.gender || null,
          sexual_orientation: normalizeOrientation(p.sexual_orientation),
          city: p.city || "",
          country: p.country || "",
          languages: p.languages || [],
        });
        setLookingFor({
          relationship_goals: p.relationship_goals || [],
          interested_in_genders: p.interested_in_genders || [],
          age_min: p.age_min ?? 25,
          age_max: p.age_max ?? 55,
          distance_km: p.distance_km ?? 100,
        });
        setBio(p.bio || "");
        setPhotos(p.photos || []);
        setInterests(p.interests || []);
        setValues({ values: p.values || [], top_values: p.top_values || [] });
        setLifestyle({
          smoking: p.smoking || null, alcohol: p.alcohol || null, pets: p.pets || null,
          lifestyle_type: p.lifestyle_type || null, diet: p.diet || null,
        });
        setFamily({
          relationship_status: p.relationship_status || null, children: p.children || null,
          wants_children: p.wants_children || null, open_to_children: p.open_to_children || null,
        });
        setEducation({ education: p.education || null, profession: p.profession || "" });
        setReligion({ religion: p.religion || null, religion_importance: p.religion_importance || null });
        setCommunity({
          covid_perspective: p.covid_perspective || null,
          covid_motivations: p.covid_motivations || [],
          covid_public: !!p.covid_public,
        });
        setComm({ communication_style: p.communication_style || [] });
        setStep(Math.min(pRes.data?.onboarding_step || 0, TOTAL - 1));
      } catch (e) {
        setError(extractError(e));
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const persistStep = useCallback(async (idx) => {
    try {
      await api.patch("/profile/onboarding-step", { step: idx + 1, completed: false });
    } catch {}
  }, []);

  const label = (kind, key) => optionLabel(lang, kind, key);

  // Save current section to backend
  const saveSection = useCallback(async () => {
    setError("");
    const name = STEPS[step];
    try {
      if (name === "basic") {
        if (!basic.display_name || !basic.birthdate) throw new Error(lang === "it" ? "Nome e data di nascita sono obbligatori." : "Display name and birthdate are required.");
        await api.patch("/profile/section/basic", Object.fromEntries(Object.entries(basic).filter(([key]) => key !== "sexual_orientation" || hasSensitiveConsent(profile, "relationships"))));
      } else if (name === "looking_for") {
        // Let the backend enforce the age-range rule so the localized
        // 'invalid_age_range' code flows through friendlyError()/ERROR_CODES.
        await api.patch("/profile/section/looking_for", Object.fromEntries(Object.entries(lookingFor).filter(([key]) => key !== "interested_in_genders" || hasSensitiveConsent(profile, "relationships"))));
      } else if (name === "about_me") {
        await api.patch("/profile/section/about_me", { bio, content_context: bioContentContext });
      } else if (name === "photos") {
        // Photos are saved live via PhotoManager — nothing to send here
      } else if (name === "interests") {
        await api.patch("/profile/section/interests", { interests });
      } else if (name === "values") {
        await api.patch("/profile/section/values", values);
      } else if (name === "lifestyle") {
        await api.patch("/profile/section/lifestyle", lifestyle);
        await api.patch("/profile/section/family", family);
        await api.patch("/profile/section/education", education);
        if (hasSensitiveConsent(profile, "religion")) await api.patch("/profile/section/religion", religion);
        await api.patch("/profile/section/communication", comm);
      } else if (name === "community") {
        if (hasSensitiveConsent(profile, "community")) await api.patch("/profile/section/community", community);
      }
    } catch (e) {
      throw e;
    }
  }, [step, basic, lookingFor, bio, bioContentContext, interests, values, lifestyle, family, education, religion, community, comm, lang, profile]);

  const next = async () => {
    setSaving(true);
    try {
      await saveSection();
      await persistStep(step);
      if (step < TOTAL - 1) setStep(step + 1);
    } catch (e) {
      setError(friendlyError(lang, extractError(e)) || e.message || (lang === "it" ? "Errore" : "Error"));
    } finally {
      setSaving(false);
    }
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  const skip = async () => {
    setSaving(true);
    try {
      await persistStep(step);
      if (step < TOTAL - 1) setStep(step + 1);
    } finally {
      setSaving(false);
    }
  };

  const finish = async () => {
    setSaving(true);
    setError("");
    try {
      await saveSection();
      await api.patch("/profile/onboarding-step", { step: TOTAL, completed: true });
      await refresh();
      navigate("/app/discover", { replace: true });
    } catch (e) {
      setError(friendlyError(lang, extractError(e)));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return <div className="text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>{t("common.loading")}</div>;
  }

  const T = {
    step: lang === "it" ? "Passo" : "Step",
    required: lang === "it" ? "Obbligatorio" : "Required",
    recommended: lang === "it" ? "Consigliato" : "Recommended",
    optional: lang === "it" ? "Facoltativo" : "Optional",
    skip: lang === "it" ? "Salta" : "Skip",
    back: lang === "it" ? "Indietro" : "Back",
    next: lang === "it" ? "Continua" : "Continue",
    finish: lang === "it" ? "Termina" : "Finish",
  };

  const stepMeta = {
    basic:        { tag: T.required,    title: lang==="it"?"Chi sei":"Who you are", subtitle: lang==="it"?"Le informazioni principali del tuo profilo.":"Your main profile details." },
    looking_for:  { tag: T.required,    title: lang==="it"?"Cosa cerchi":"What you're looking for", subtitle: lang==="it"?"Il tipo di connessioni che desideri e chi vorresti incontrare.":"The kind of connection and who you'd like to meet." },
    about_me:     { tag: T.recommended, title: lang==="it"?"Presentati":"Introduce yourself", subtitle: lang==="it"?"Racconta qualcosa di autentico su di te.":"Tell people something genuine about yourself." },
    photos:       { tag: T.recommended, title: lang==="it"?"Le tue foto":"Your photos", subtitle: lang==="it"?"Fino a 8 foto. La prima è la principale.":"Up to 8 photos. The first is your main photo." },
    interests:    { tag: T.recommended, title: lang==="it"?"Interessi":"Interests", subtitle: lang==="it"?"Almeno 5 per migliorare la compatibilità (consigliato).":"At least 5 to improve compatibility (recommended)." },
    values:       { tag: T.recommended, title: lang==="it"?"I tuoi valori":"Your values", subtitle: lang==="it"?"Scegli i valori che condividi e i 5 più importanti.":"Choose the values you share and the 5 most important." },
    lifestyle:    { tag: T.optional,    title: lang==="it"?"Stile di vita":"Lifestyle", subtitle: lang==="it"?"Facoltativo. Aiuta la compatibilità.":"Optional. Helps compatibility." },
    community:    { tag: T.optional,    title: lang==="it"?"Community":"Community", subtitle: lang==="it"?"Facoltativo. Nessuna prova richiesta. Puoi scegliere se rendere pubblici questi dati.":"Optional. No proof required. You control whether it's public." },
  };

  const current = STEPS[step];
  const meta = stepMeta[current];

  // ---- Render each step ----
  const renderStep = () => {
    if (current === "basic") return (
      <div className="space-y-5">
        <div>
          <FieldLabel required>{lang==="it"?"Nome":"First name"}</FieldLabel>
          <input className="nvl-input mt-2" value={basic.first_name} onChange={(e)=>setBasic({...basic, first_name:e.target.value})} data-testid="ob-first-name" />
        </div>
        <div>
          <FieldLabel required>{lang==="it"?"Nome visualizzato":"Display name"}</FieldLabel>
          <input className="nvl-input mt-2" value={basic.display_name} onChange={(e)=>setBasic({...basic, display_name:e.target.value})} data-testid="ob-display-name" />
        </div>
        <div>
          <FieldLabel required>{lang==="it"?"Data di nascita":"Birthdate"}</FieldLabel>
          <input type="date" className="nvl-input mt-2" value={basic.birthdate} onChange={(e)=>setBasic({...basic, birthdate:e.target.value})} data-testid="ob-birthdate" max={new Date(new Date().setFullYear(new Date().getFullYear()-18)).toISOString().split("T")[0]} />
        </div>
        <div>
          <FieldLabel recommended>{lang==="it"?"Genere":"Gender"}</FieldLabel>
          <div className="mt-2"><ChipGroup options={options.gender||[]} value={basic.gender} onChange={(v)=>setBasic({...basic, gender:v})} labelFor={(o)=>label("gender",o)} testIdPrefix="ob-gender" /></div>
        </div>
        <div>
          <FieldLabel optional>{lang==="it"?"Orientamento sessuale":"Sexual orientation"}</FieldLabel>
          <SensitiveField profile={profile} group="relationships"><div className="mt-2"><ChipGroup options={options.sexual_orientation||[]} value={basic.sexual_orientation} onChange={(v)=>setBasic({...basic, sexual_orientation:v})} labelFor={(o)=>label("sexual_orientation",o)} testIdPrefix="ob-orientation" /></div></SensitiveField>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel recommended>{lang==="it"?"Paese":"Country"}</FieldLabel>
            <input className="nvl-input mt-2" value={basic.country} onChange={(e)=>setBasic({...basic, country:e.target.value})} data-testid="ob-country" />
          </div>
          <div>
            <FieldLabel recommended>{lang==="it"?"Città":"City"}</FieldLabel>
            <input className="nvl-input mt-2" value={basic.city} onChange={(e)=>setBasic({...basic, city:e.target.value})} data-testid="ob-city" />
          </div>
        </div>
        <div>
          <FieldLabel optional>{lang==="it"?"Lingue parlate":"Languages spoken"}</FieldLabel>
          <div className="mt-2"><ChipGroup multi max={8} options={options.languages||[]} value={basic.languages} onChange={(v)=>setBasic({...basic, languages:v})} labelFor={(o)=>label("languages",o)} testIdPrefix="ob-lang" /></div>
        </div>
      </div>
    );

    if (current === "looking_for") return (
      <div className="space-y-6">
        <div>
          <FieldLabel required>{lang==="it"?"Cosa cerchi":"What you're looking for"}</FieldLabel>
          <p className="text-xs mt-1" style={{color:"hsl(var(--nvl-ink-soft))"}}>{lang==="it"?"Selezione multipla":"Multi-select"}</p>
          <div className="mt-3"><ChipGroup multi options={options.relationship_goals||[]} value={lookingFor.relationship_goals} onChange={(v)=>setLookingFor({...lookingFor, relationship_goals:v})} labelFor={(o)=>label("relationship_goals",o)} testIdPrefix="ob-goal" /></div>
        </div>
        <div>
          <FieldLabel optional>{lang==="it"?"Chi ti interessa":"Interested in"}</FieldLabel>
          <SensitiveField profile={profile} group="relationships"><div className="mt-3"><ChipGroup multi options={options.interested_in||[]} value={lookingFor.interested_in_genders} onChange={(v)=>setLookingFor({...lookingFor, interested_in_genders:v})} labelFor={(o)=>label("interested_in",o)} testIdPrefix="ob-interested" /></div></SensitiveField>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel>{lang==="it"?"Età min":"Age min"}</FieldLabel>
            <input type="number" min={18} max={99} className="nvl-input mt-2" value={lookingFor.age_min} onChange={(e)=>setLookingFor({...lookingFor, age_min:Number(e.target.value)})} data-testid="ob-age-min" />
          </div>
          <div>
            <FieldLabel>{lang==="it"?"Età max":"Age max"}</FieldLabel>
            <input type="number" min={18} max={99} className="nvl-input mt-2" value={lookingFor.age_max} onChange={(e)=>setLookingFor({...lookingFor, age_max:Number(e.target.value)})} data-testid="ob-age-max" />
          </div>
        </div>
        <div>
          <FieldLabel>{lang==="it"?"Distanza (km)":"Distance (km)"}</FieldLabel>
          <input type="number" min={1} max={500} className="nvl-input mt-2" value={lookingFor.distance_km} onChange={(e)=>setLookingFor({...lookingFor, distance_km:Number(e.target.value)})} data-testid="ob-distance" />
        </div>
      </div>
    );

    if (current === "about_me") return (
      <div>
        <FieldLabel recommended>{lang==="it"?"Su di me":"About me"}</FieldLabel>
        <textarea className="nvl-input mt-2 min-h-[200px]" maxLength={1000} value={bio} onChange={(e)=>setBio(e.target.value)} data-testid="ob-bio" placeholder={lang==="it"?"Racconta qualcosa di autentico su di te…":"Tell people something genuine about yourself…"} />
        <div className="mt-2 text-xs" style={{color:"hsl(var(--nvl-ink-soft))"}}>{bio.length} / 1000</div>
      </div>
    );

    if (current === "photos") return (
      <div>
        <PhotoManager photos={photos} onChange={setPhotos} />
      </div>
    );

    if (current === "interests") return (
      <div>
        <FieldLabel recommended>{lang==="it"?"I tuoi interessi":"Your interests"}</FieldLabel>
        <p className="text-xs mt-1" style={{color:"hsl(var(--nvl-ink-soft))"}}>{lang==="it"?"Consigliato almeno 5.":"At least 5 recommended."}</p>
        <div className="mt-3"><ChipGroup multi options={options.interests||[]} value={interests} onChange={setInterests} labelFor={(o)=>label("interests",o)} testIdPrefix="ob-int" /></div>
        <div className="mt-3 text-xs" style={{color:"hsl(var(--nvl-ink-soft))"}}>{interests.length} {lang==="it"?"selezionati":"selected"}</div>
      </div>
    );

    if (current === "values") return (
      <div className="space-y-6">
        <div>
          <FieldLabel recommended>{lang==="it"?"I tuoi valori":"Your values"}</FieldLabel>
          <div className="mt-3"><ChipGroup multi options={options.values||[]} value={values.values} onChange={(v)=>setValues({...values, values:v, top_values:values.top_values.filter(x=>v.includes(x))})} labelFor={(o)=>label("values",o)} testIdPrefix="ob-val" /></div>
        </div>
        <div>
          <FieldLabel recommended>{lang==="it"?"I 5 più importanti":"Top 5"}</FieldLabel>
          <div className="mt-3"><ChipGroup multi max={5} options={values.values} value={values.top_values} onChange={(v)=>setValues({...values, top_values:v})} labelFor={(o)=>label("values",o)} testIdPrefix="ob-topval" /></div>
          <p className="mt-2 text-xs" style={{color:"hsl(var(--nvl-ink-soft))"}}>{values.top_values.length} / 5</p>
        </div>
      </div>
    );

    if (current === "lifestyle") return (
      <div className="space-y-6">
        <div>
          <FieldLabel optional>{lang==="it"?"Stile di vita":"Lifestyle type"}</FieldLabel>
          <div className="mt-2"><ChipGroup options={options.lifestyle_type||[]} value={lifestyle.lifestyle_type} onChange={(v)=>setLifestyle({...lifestyle, lifestyle_type:v})} labelFor={(o)=>label("lifestyle_type",o)} testIdPrefix="ob-lifestyle-type" /></div>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <FieldLabel optional>{lang==="it"?"Fumo":"Smoking"}</FieldLabel>
            <div className="mt-2"><ChipGroup options={options.smoking||[]} value={lifestyle.smoking} onChange={(v)=>setLifestyle({...lifestyle, smoking:v})} labelFor={(o)=>label("smoking",o)} testIdPrefix="ob-smoking" /></div>
          </div>
          <div>
            <FieldLabel optional>{lang==="it"?"Alcol":"Alcohol"}</FieldLabel>
            <div className="mt-2"><ChipGroup options={options.alcohol||[]} value={lifestyle.alcohol} onChange={(v)=>setLifestyle({...lifestyle, alcohol:v})} labelFor={(o)=>label("alcohol",o)} testIdPrefix="ob-alcohol" /></div>
          </div>
          <div>
            <FieldLabel optional>{lang==="it"?"Animali":"Pets"}</FieldLabel>
            <div className="mt-2"><ChipGroup options={options.pets||[]} value={lifestyle.pets} onChange={(v)=>setLifestyle({...lifestyle, pets:v})} labelFor={(o)=>label("pets",o)} testIdPrefix="ob-pets" /></div>
          </div>
          <div>
            <FieldLabel optional>{lang==="it"?"Alimentazione":"Diet"}</FieldLabel>
            <div className="mt-2"><ChipGroup options={options.diet||[]} value={lifestyle.diet} onChange={(v)=>setLifestyle({...lifestyle, diet:v})} labelFor={(o)=>label("diet",o)} testIdPrefix="ob-diet" /></div>
          </div>
        </div>

        <div className="nvl-divider" />

        <p className="nvl-overline" style={{color:"hsl(var(--nvl-ink-soft))"}}>{lang==="it"?"Famiglia":"Family"}</p>
        <div className="grid md:grid-cols-2 gap-6">
          <div><FieldLabel optional>{lang==="it"?"Stato civile":"Relationship status"}</FieldLabel>
            <div className="mt-2"><ChipGroup options={options.relationship_status||[]} value={family.relationship_status} onChange={(v)=>setFamily({...family, relationship_status:v})} labelFor={(o)=>label("relationship_status",o)} testIdPrefix="ob-rstatus" /></div>
          </div>
          <div><FieldLabel optional>{lang==="it"?"Figli":"Children"}</FieldLabel>
            <div className="mt-2"><ChipGroup options={options.children||[]} value={family.children} onChange={(v)=>setFamily({...family, children:v})} labelFor={(o)=>label("children",o)} testIdPrefix="ob-children" /></div>
          </div>
          <div><FieldLabel optional>{lang==="it"?"Desiderio di figli":"Wants children"}</FieldLabel>
            <div className="mt-2"><ChipGroup options={options.wants_children||[]} value={family.wants_children} onChange={(v)=>setFamily({...family, wants_children:v})} labelFor={(o)=>label("wants_children",o)} testIdPrefix="ob-wchildren" /></div>
          </div>
          <div><FieldLabel optional>{lang==="it"?"Aperto a partner con figli":"Open to partner with children"}</FieldLabel>
            <div className="mt-2"><ChipGroup options={options.open_to_children||[]} value={family.open_to_children} onChange={(v)=>setFamily({...family, open_to_children:v})} labelFor={(o)=>label("open_to_children",o)} testIdPrefix="ob-otcchildren" /></div>
          </div>
        </div>

        <div className="nvl-divider" />

        <p className="nvl-overline" style={{color:"hsl(var(--nvl-ink-soft))"}}>{lang==="it"?"Istruzione e professione":"Education & Profession"}</p>
        <div>
          <FieldLabel optional>{lang==="it"?"Istruzione":"Education"}</FieldLabel>
          <div className="mt-2"><ChipGroup options={options.education||[]} value={education.education} onChange={(v)=>setEducation({...education, education:v})} labelFor={(o)=>label("education",o)} testIdPrefix="ob-edu" /></div>
        </div>
        <div>
          <FieldLabel optional>{lang==="it"?"Professione":"Profession"}</FieldLabel>
          <input className="nvl-input mt-2" value={education.profession} onChange={(e)=>setEducation({...education, profession:e.target.value})} data-testid="ob-profession" />
        </div>

        <div className="nvl-divider" />

        <p className="nvl-overline" style={{color:"hsl(var(--nvl-ink-soft))"}}>{lang==="it"?"Religione e spiritualità":"Religion & spirituality"}</p>
        <div>
          <FieldLabel optional>{lang==="it"?"Religione":"Religion"}</FieldLabel>
          <SensitiveField profile={profile} group="religion"><div className="mt-2"><ChipGroup options={options.religion||[]} value={religion.religion} onChange={(v)=>setReligion({...religion, religion:v})} labelFor={(o)=>label("religion",o)} testIdPrefix="ob-religion" /></div></SensitiveField>
        </div>
        <div>
          <FieldLabel optional>{lang==="it"?"Importanza":"Importance"}</FieldLabel>
          <SensitiveField profile={profile} group="religion"><div className="mt-2"><ChipGroup options={options.religion_importance||[]} value={religion.religion_importance} onChange={(v)=>setReligion({...religion, religion_importance:v})} labelFor={(o)=>label("religion_importance",o)} testIdPrefix="ob-relimp" /></div></SensitiveField>
        </div>

        <div className="nvl-divider" />

        <p className="nvl-overline" style={{color:"hsl(var(--nvl-ink-soft))"}}>{lang==="it"?"Stile di comunicazione":"Communication style"}</p>
        <div><ChipGroup multi options={options.communication_style||[]} value={comm.communication_style} onChange={(v)=>setComm({communication_style:v})} labelFor={(o)=>label("communication_style",o)} testIdPrefix="ob-comm" /></div>
      </div>
    );

    if (current === "community") return (
      <div className="space-y-6">
        <div className="nvl-card p-5" style={{ backgroundColor: "hsl(var(--muted))" }}>
          <p className="text-sm" style={{color:"hsl(var(--nvl-ink-soft))"}}>
            {lang==="it"
              ? "NO VAX LOVE non richiede prove sullo stato vaccinale. Questa sezione è facoltativa e puoi decidere se renderla pubblica o meno."
              : "NO VAX LOVE never requires proof of vaccination status. This section is optional and you control whether it appears on your public profile."}
          </p>
        </div>
        <div>
          <FieldLabel optional>{lang==="it"?"La tua prospettiva sul COVID-19":"Your COVID-19 perspective"}</FieldLabel>
          <SensitiveField profile={profile} group="community"><div className="mt-2"><ChipGroup options={options.covid_perspective||[]} value={community.covid_perspective} onChange={(v)=>setCommunity({...community, covid_perspective:v})} labelFor={(o)=>label("covid_perspective",o)} testIdPrefix="ob-covid" /></div></SensitiveField>
        </div>
        <div>
          <FieldLabel optional>{lang==="it"?"Cosa ti porta in NO VAX LOVE?":"What brings you to NO VAX LOVE?"}</FieldLabel>
          <p className="text-xs mt-1" style={{color:"hsl(var(--nvl-ink-soft))"}}>{lang==="it"?"Selezione multipla":"Multi-select"}</p>
          <SensitiveField profile={profile} group="community"><div className="mt-2"><ChipGroup multi options={options.covid_motivations||[]} value={community.covid_motivations} onChange={(v)=>setCommunity({...community, covid_motivations:v})} labelFor={(o)=>label("covid_motivations",o)} testIdPrefix="ob-covidm" /></div></SensitiveField>
        </div>
        <label className="flex items-start gap-3 text-sm cursor-pointer">
          <input type="checkbox" disabled={!hasSensitiveConsent(profile, "community", "sharing")} checked={community.covid_public} onChange={(e)=>setCommunity({...community, covid_public:e.target.checked})} data-testid="ob-covid-public" className="mt-1 h-4 w-4" />
          <span>{lang==="it"?"Rendi visibile la mia prospettiva sul mio profilo pubblico.":"Show my perspective on my public profile."}</span>
        </label>
      </div>
    );

    return null;
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="onboarding-page">
      <SensitiveConsents profile={profile} onSaved={async next => {
        setProfile(next);
        setBasic(v => ({...v, sexual_orientation: normalizeOrientation(next.sexual_orientation)}));
        setLookingFor(v => ({...v, interested_in_genders: next.interested_in_genders || []}));
        setReligion({religion: next.religion || null, religion_importance: next.religion_importance || null});
        setCommunity({covid_perspective: next.covid_perspective || null, covid_motivations: next.covid_motivations || [], covid_public: !!next.covid_public});
        await refresh();
      }} />
      <p className="nvl-overline" style={{color:"hsl(var(--nvl-accent))"}}>NO VAX LOVE</p>
      <h1 className="mt-3 text-3xl md:text-4xl">{lang==="it"?"Crea il tuo profilo":"Build your profile"}</h1>
      <div className="mt-6 flex gap-1" data-testid="onboarding-progress">
        {STEPS.map((_, i) => (
          <div key={i} className="h-1 flex-1 rounded-full" style={{backgroundColor: i<=step? "hsl(var(--nvl-accent))" : "hsl(var(--nvl-border))"}} />
        ))}
      </div>

      <div className="mt-10 nvl-card p-6 md:p-8">
        <StepShell step={step+1} total={TOTAL} title={meta.title} subtitle={meta.subtitle} tag={meta.tag}>
          {renderStep()}
        </StepShell>

        {error ? (
          <div className="mt-6 text-sm" style={{color:"hsl(var(--destructive))"}} data-testid="onboarding-error">{error}</div>
        ) : null}

        <div className="mt-10 flex justify-between items-center gap-2 flex-wrap">
          <button type="button" onClick={back} disabled={step===0 || saving} data-testid="onboarding-back" className="nvl-btn nvl-btn-outline disabled:opacity-40">
            <ArrowLeft size={16} className="mr-2" /> {T.back}
          </button>
          <div className="flex gap-2">
            {meta.tag !== T.required ? (
              <button type="button" onClick={skip} disabled={saving} data-testid="onboarding-skip" className="nvl-btn nvl-btn-outline">
                <SkipForward size={16} className="mr-2" /> {T.skip}
              </button>
            ) : null}
            {step < TOTAL - 1 ? (
              <button type="button" onClick={next} disabled={saving} data-testid="onboarding-next" className="nvl-btn nvl-btn-primary">
                {saving? t("common.loading") : T.next} <ArrowRight size={16} className="ml-2" />
              </button>
            ) : (
              <button type="button" onClick={finish} disabled={saving} data-testid="onboarding-submit" className="nvl-btn nvl-btn-primary">
                {saving? t("common.loading") : T.finish} <Check size={16} className="ml-2" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
